// @flow

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Dialog,
  Alert,
  Button,
  Classes,
  InputGroup,
  Intent,
  Icon,
} from '@blueprintjs/core';
import type { CategoryMeta, Option } from './categorized-picker-popover';

export type { CategoryMeta, Option } from './categorized-picker-popover';

export type Mode = 'add' | 'remove';

type Copy = {
  addTitle: string,
  removeTitle: string,
  addConfirmButton: string,
  removeConfirmButton: string,
  categorySectionLabel: string,
};

type Props = {
  isOpen: boolean,
  mode: Mode,
  selectionCount: number,
  categories: $ReadOnlyArray<CategoryMeta>,
  options: $ReadOnlyArray<Option>,
  singleSelectCategories: $ReadOnlyArray<string>,
  copy: Copy,
  renderConfirmation: (option: Option, count: number, mode: Mode) => React$Node,
  onClose: () => mixed,
  onConfirm: (option: Option) => mixed,
};

const Body = styled.div`
  padding: 16px 20px;
`;

const Section = styled.div`
  margin-bottom: 14px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5c7080;
  margin-bottom: 8px;
`;

const TypeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TypeCard = styled.button`
  border: 1px solid ${(p) => (p.selected ? '#137cbd' : '#d1d8de')};
  background: ${(p) => (p.selected ? '#ebf3fa' : 'white')};
  border-radius: 6px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    border-color: #137cbd;
  }
  &:focus {
    outline: 1px solid #137cbd;
    outline-offset: 1px;
  }
`;

const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.color};
`;

const ValueList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ebf1f5;
  border-radius: 4px;
`;

const ValueItem = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: ${(p) => (p.selected ? '#ebf3fa' : 'transparent')};
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background: ${(p) => (p.selected ? '#ebf3fa' : '#f5f8fa')};
  }
`;

const Empty = styled.div`
  padding: 14px;
  font-size: 12px;
  color: #5c7080;
  text-align: center;
  font-style: italic;
`;

const SubHeader = styled.div`
  padding: 8px 12px 4px 12px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #5c7080;
  font-weight: 600;
  background: #f5f8fa;
`;

const findCategory = (
  categories: $ReadOnlyArray<CategoryMeta>,
  key: string,
): ?CategoryMeta => categories.find((c) => c.key === key);

// `singleSelectCategories` is part of the Props contract for forward-compat
// with workstream C's bulk dialog usage; this iteration doesn't read it
// internally — replace semantics live in the caller's `renderConfirmation`.
const CategorizedBulkPickerDialog = ({
  isOpen,
  mode,
  selectionCount,
  categories,
  options,
  copy,
  renderConfirmation,
  onClose,
  onConfirm,
}: Props) => {
  const [pickedCategory, setPickedCategory] = useState<?string>(null);
  const [pickedOptionId, setPickedOptionId] = useState<?string>(null);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPickedCategory(null);
      setPickedOptionId(null);
      setSearch('');
      setConfirmOpen(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  const valuesForCategory = useMemo(() => {
    if (!pickedCategory) return [];
    const lower = search.toLowerCase();
    const filtered = options.filter(
      (o) =>
        o.categoryKey === pickedCategory &&
        (lower === '' || o.label.toLowerCase().includes(lower)),
    );
    const anySubType = filtered.some((o) => !!o.subTypeKey);
    if (!anySubType) {
      return filtered.slice().sort((a, b) => a.label.localeCompare(b.label));
    }
    return filtered.slice();
  }, [pickedCategory, search, options]);

  const groupedValues = useMemo(() => {
    const groups: Array<{ subTypeKey: ?string, subTypeLabel: ?string, items: Option[] }> = [];
    let last = null;
    valuesForCategory.forEach((opt) => {
      const key = opt.subTypeKey || null;
      if (!last || last.subTypeKey !== key) {
        last = {
          subTypeKey: key,
          subTypeLabel: opt.subTypeLabel || opt.subTypeKey || null,
          items: [],
        };
        groups.push(last);
      }
      last.items.push(opt);
    });
    return groups;
  }, [valuesForCategory]);

  const pickedOption = useMemo(() => {
    if (!pickedOptionId) return null;
    return options.find((o) => o.id === pickedOptionId) || null;
  }, [pickedOptionId, options]);

  const handleContinue = () => {
    if (!pickedOption) return;
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!pickedOption) return;
    setSubmitting(true);
    let ok = true;
    try {
      await onConfirm(pickedOption);
    } catch (e) {
      // Caller is responsible for surfacing the error (toast etc.).
      // Keep the outer dialog open so the user can retry without re-staging.
      ok = false;
    }
    setSubmitting(false);
    setConfirmOpen(false);
    if (ok) onClose();
  };

  const title = mode === 'add' ? copy.addTitle : copy.removeTitle;
  const pickedCategoryMeta = pickedCategory
    ? findCategory(categories, pickedCategory)
    : null;

  return (
    <>
      <Dialog
        isOpen={isOpen && !confirmOpen}
        onClose={onClose}
        title={title}
        icon={mode === 'add' ? 'plus' : 'cross'}
        canOutsideClickClose={!submitting}
        canEscapeKeyClose={!submitting}
      >
        <Body>
          <Section>
            <SectionLabel>{copy.categorySectionLabel}</SectionLabel>
            <TypeRow>
              {categories.map((cat) => (
                <TypeCard
                  key={cat.key}
                  type="button"
                  selected={pickedCategory === cat.key}
                  onClick={() => {
                    setPickedCategory(cat.key);
                    setPickedOptionId(null);
                    setSearch('');
                  }}
                >
                  <Dot color={cat.color} />
                  {cat.label}
                </TypeCard>
              ))}
            </TypeRow>
          </Section>
          {pickedCategory && pickedCategoryMeta && (
            <Section>
              <SectionLabel>{pickedCategoryMeta.label} value</SectionLabel>
              <div style={{ marginBottom: 8 }}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <ValueList>
                {valuesForCategory.length === 0 ? (
                  <Empty>No values available</Empty>
                ) : (
                  groupedValues.map((g, gi) => (
                    <React.Fragment key={g.subTypeKey || `__flat_${gi}`}>
                      {g.subTypeKey && (
                        <SubHeader>{g.subTypeLabel || g.subTypeKey}</SubHeader>
                      )}
                      {g.items.map((opt) => (
                        <ValueItem
                          type="button"
                          key={opt.id}
                          selected={pickedOptionId === opt.id}
                          onClick={() => setPickedOptionId(opt.id)}
                        >
                          {pickedOptionId === opt.id && <Icon icon="tick" />}
                          <span>{opt.label}</span>
                        </ValueItem>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </ValueList>
            </Section>
          )}
        </Body>
        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={onClose} text="Cancel" />
            <Button
              intent={Intent.PRIMARY}
              disabled={!pickedOptionId}
              onClick={handleContinue}
              text="Continue"
            />
          </div>
        </div>
      </Dialog>
      <Alert
        isOpen={confirmOpen}
        cancelButtonText="Back"
        confirmButtonText={
          mode === 'add' ? copy.addConfirmButton : copy.removeConfirmButton
        }
        intent={mode === 'add' ? Intent.PRIMARY : Intent.DANGER}
        icon={mode === 'add' ? 'tag' : 'trash'}
        loading={submitting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      >
        {pickedOption && renderConfirmation(pickedOption, selectionCount, mode)}
      </Alert>
    </>
  );
};

export default CategorizedBulkPickerDialog;
